/**
 * mmc_tab_system.js
 * Responsive tab system component (mobile-first)
 */

import React, { useState, useEffect, Suspense } from 'react';
import { List, X, Browser, Cube } from '@phosphor-icons/react';
import MmcNavCategories from './mmc_nav_categories';
import MmcGlobalSearch from './mmc_global_search';
import MmcCookieGuardian from './mmc_cookie_guardian';
import MmcLegalModal from './mmc_legal_modal';
import MmcDashboard from './mmc_dashboard';
import logo from '../assets/img/madmoco_logo_black_360X80.svg';
import '../styles/mmc-tab-system.css';
import '../styles/mmc-nav-categories.css';

import { MmcStack, MmcLabel, MmcBadge } from './ui';

const MmcTabSystem = ({ modules = [], registry = null }) => {
  const [active_tab, set_active_tab] = useState(null);
  const [mobile_menu_open, set_mobile_menu_open] = useState(false);
  const [available_modules, set_available_modules] = useState([]);
  const [legal_modal, set_legal_modal] = useState({ open: false, type: null });

  useEffect(() => {
    // Ensure sidebar is visible by default on desktop
    try {
      if (window && window.innerWidth >= 1024) {
        set_mobile_menu_open(true);
      }
    } catch (e) {
      // ignore
    }

    const enabled = modules
      .filter(m => m.enabled !== false)
      .sort((a, b) => (a.order || 999) - (b.order || 999));

    set_available_modules(enabled);
  }, [modules, active_tab, registry]);

  const handle_logo_click = () => {
    set_active_tab(null);
    if (window.innerWidth < 1024) {
      set_mobile_menu_open(false);
    }
  };

  // Update document title for SEO and A11y
  useEffect(() => {
    const active = available_modules.find(m => m.id === active_tab);
    if (active) {
      document.title = `${active.display_name} | Madmoco DevTools`;
    } else {
      document.title = `Madmoco DevTools - Developer Toolbox`;
    }
  }, [active_tab, available_modules]);

  const handle_tab_click = (module_id) => {
    set_active_tab(module_id);
    if (window.innerWidth < 1024) {
      set_mobile_menu_open(false);
    }
    // Focus management: when tab changes, optionally move focus to main content
    const mainContent = document.getElementById('mmc-main-content');
    if (mainContent) mainContent.focus();
  };

  const active_module = available_modules.find(m => m.id === active_tab);

  const get_component = (module_id) => {
    if (registry) {
      const comp = registry.get_component(module_id);
      if (comp) return comp;
    }
    const manifest = available_modules.find(m => m.id === module_id);
    return manifest?.component || null;
  };

  const ActiveComponent = active_module ? get_component(active_module.id) : null;

  return (
    <div className="mmc-tab-system">
      <a href="#mmc-main-content" className="mmc-skip-link">Skip to content</a>

      <MmcGlobalSearch modules={available_modules} onSelect={handle_tab_click} />

      {/* Mobile Header */}
      <header className="mmc-tab-system__mobile-header">
        <div className="mmc-tab-system__mobile-logo" onClick={handle_logo_click} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="Madmoco Logo" className="mmc-logo-mobile" width="360" height="80" />
        </div>
        <button
          className="mmc-tab-system__menu-toggle"
          onClick={() => set_mobile_menu_open(!mobile_menu_open)}
          aria-label={mobile_menu_open ? "Close menu" : "Open menu"}
          aria-expanded={mobile_menu_open}
        >
          {mobile_menu_open ? <X size={28} weight="bold" /> : <List size={28} weight="bold" />}
        </button>
      </header>

      {/* Navigation Tray */}
      <nav
        className={`mmc-tab-system__nav ${mobile_menu_open ? 'mmc-tab-system__nav--open' : ''}`}
        aria-label="Main Tool Navigation"
      >
        <div className="mmc-tab-system__nav-header" onClick={handle_logo_click} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="Madmoco Logo" className="mmc-logo-nav" width="360" height="80" />
        </div>

        <MmcNavCategories
          modules={available_modules}
          onModuleSelect={handle_tab_click}
          activeTab={active_tab}
        />

        <footer className="mmc-tab-system__nav-footer">
          <div className="mmc-tab-system__footer-content">
            <div className="mmc-tab-system__footer-main">
              &copy; {new Date().getFullYear()} <a href="https://www.madmoco.com" target="_blank" rel="noopener noreferrer" className="mmc-tab-system__footer-link">MADMOCO</a>.TOOLBOX
            </div>
            <div className="mmc-tab-system__footer-legal">
              <button onClick={() => set_legal_modal({ open: true, type: 'privacy' })} className="mmc-legal-link">PRIVACY</button>
              <button onClick={() => set_legal_modal({ open: true, type: 'cookies' })} className="mmc-legal-link">COOKIES</button>
              <button onClick={() => set_legal_modal({ open: true, type: 'terms' })} className="mmc-legal-link">TERMS</button>
            </div>
          </div>
        </footer>
      </nav>

      {/* Main Content */}
      <main
        id="mmc-main-content"
        className="mmc-tab-system__content"
        tabIndex="-1"
      >
        {ActiveComponent ? (
          <article>
            <MmcStack gap={24}>
              {/* Local module header to identify active tool */}
              <MmcLabel icon={Cube} className="mmc-tab-system__active-tool-label">
                <h1>{active_module.display_name}</h1>
              </MmcLabel>
              <Suspense fallback={
                <div className="mmc-loader-container">
                  <div className="mmc-loader"></div>
                  <p>Initializing Tool...</p>
                </div>
              }>
                <ActiveComponent />
              </Suspense>
            </MmcStack>
          </article>
        ) : (
          <MmcDashboard
            modules={available_modules}
            onSelectModule={handle_tab_click}
          />
        )}
      </main>

      <MmcCookieGuardian />

      <MmcLegalModal
        type={legal_modal.type}
        isOpen={legal_modal.open}
        onClose={() => set_legal_modal({ open: false, type: null })}
      />
    </div>
  );
};

export default MmcTabSystem;
